import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import { keyBy } from "lodash";

// Trigger actions for saga compatibility (saga watches these)
export const fetchPrograms = createAction<any>("programs/triggerAll");
export const fetchProgram = createAction<any>("programs/triggerSingle");

interface ProgramsState {
  loading: boolean;
  loaded: boolean;
  failed: boolean;
  data: Record<string, any>;
}

const initialState: ProgramsState = {
  loading: false, // Whether we're currently loading items
  loaded: false, // Whether we've attemped to load items yet (leave true once set)
  failed: false, // Whether latest request for item/items failed (set false on new starts)
  data: {}, // key by id
};

const programsSlice = createSlice({
  name: "programs",
  initialState,
  reducers: {
    // Multiple
    fetchProgramsStart(state) {
      state.loading = true;
    },
    fetchProgramsSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.loaded = true;
      state.data = {
        // completely overwrite data; decorate EVERY program with status flags
        // (lodash merge aligns arrays by index, so it only ever decorated data[0])
        ...keyBy(
          (action.payload.data as any[]).map((program) => ({
            ...program,
            loading: false,
            loaded: true,
            failed: false,
          })),
          "id"
        ),
      };
    },
    fetchProgramsFailure() {
      return {
        ...initialState,
        failed: true,
        loading: false,
      };
    },
    // Single
    fetchProgramStart(state, action: PayloadAction<any>) {
      const id = action.payload;
      if (!state.data[id]) {
        state.data[id] = {};
      }
      state.data[id].loaded = false;
      state.data[id].loading = true;
      state.data[id].failed = false;
    },
    fetchProgramSuccess(state, action: PayloadAction<any>) {
      const program = action.payload.data;
      state.data[program.id] = {
        ...program,
        loaded: true,
        loading: false,
        failed: false,
      };
    },
    fetchProgramFailure(state, _action: PayloadAction<any>) {
      // Not a lot we can do for a failed request, so just return state unchanged.
      // @TODO Remove item from redux store or flag w/errors (not sure yet how to get its id)
    },
  },
});

export const {
  fetchProgramsStart,
  fetchProgramsSuccess,
  fetchProgramsFailure,
  fetchProgramStart,
  fetchProgramSuccess,
  fetchProgramFailure,
} = programsSlice.actions;

export default programsSlice.reducer;
